import { assertValue } from '@shared/helpers'
import {
  IfpCompanyForm,
  IfpEmploymentType,
  IfpOccupation,
  IfpWorkingHours,
  SecLoanCarOwnershipType,
  SecLoanOtherIncomeType,
  SHORT_TEXT_INPUT_MAX_LENGTH,
  TimeUntilEmployedAgainEstimation,
} from '@shared/sec-loans'
import { PHONE_COUNTRY_CODE, YesNo } from '@ui-common/types'
import {
  MINIMUM_REASONABLE_YEAR,
  testIsValidMonetaryValue,
  toNumber,
  validateBusinessId,
  validateFutureMonthAndYear,
  validatePastMonthAndYear,
  validatePhoneNumberAsync,
  validateShortTextInput,
} from '@ui-common/utils'
import { FieldName } from 'react-hook-form'
import { IntlShape } from 'react-intl'
import { create, enforce, group, omitWhen, only, Suite, test } from 'vest'

import { ApplicantInfoFormData as FormData } from './types'

export const createFormValidator = (
  formatMessage: IntlShape['formatMessage'],
): Suite<string, string, (data: FormData, changedFields?: FieldName<FormData>[]) => void> => {
  return create((data, changedFields) => {
    only(changedFields)

    group('phoneNumber', () => {
      test('phoneNumber', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.phoneNumber).isNotBlank()
      })

      test('phoneNumber', () => {
        enforce(data.phoneNumber).hasAllowedCharsWithSpaceOnly(formatMessage)
      })

      test('phoneNumber', formatMessage({ id: 'validate-finnish-mobile-phone-format-error' }), () => {
        enforce(data.phoneNumber).isValidFinnishMobilePhoneNumber()
      })

      const phoneNumberInfo = {
        phoneCountryCode: PHONE_COUNTRY_CODE,
        phoneNumber: assertValue(data.phoneNumber, 'data.phoneNumber'),
      }

      validatePhoneNumberAsync({
        fieldName: 'phoneNumber',
        phoneNumber: phoneNumberInfo,
        formatMessage,
      })
    })

    test('occupation', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.occupation).inside(Object.values(IfpOccupation))
    })

    omitWhen(data.occupation !== IfpOccupation.Pensioner, () => {
      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'grossSalary',
        incomeAfterTaxesField: 'netSalary',
        beforeTaxesLabelKey: 'sec-loans-income-info-gross-salary-for-pensioners-form-label',
        afterTaxesLabelKey: 'sec-loans-income-info-net-salary-for-pensioners-form-label',
      })
    })

    omitWhen(data.occupation !== IfpOccupation.Employed, () => {
      test('employmentType', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.employmentType).inside(Object.values(IfpEmploymentType))
      })

      validateShortTextInput('employer', formatMessage, data.employer, SHORT_TEXT_INPUT_MAX_LENGTH, true)

      test('workingHours', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.workingHours).inside(Object.values(IfpWorkingHours))
      })

      validateShortTextInput('profession', formatMessage, data.profession, SHORT_TEXT_INPUT_MAX_LENGTH, true)

      validatePastMonthAndYear({
        fieldName: 'employmentStartDate',
        dateToValidate: data.employmentStartDate,
        formatMessage,
        minimumYear: MINIMUM_REASONABLE_YEAR,
        isRequired: true,
      })

      omitWhen(data.employmentType !== IfpEmploymentType.TimeLimited, () => {
        validateFutureMonthAndYear({
          fieldName: 'employmentEndDate',
          dateToValidate: data.employmentEndDate,
          formatMessage,
          minimumYear: MINIMUM_REASONABLE_YEAR,
          isRequired: true,
        })
      })

      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'grossSalary',
        incomeAfterTaxesField: 'netSalary',
        beforeTaxesLabelKey: 'sec-loans-income-info-gross-salary-form-label',
        afterTaxesLabelKey: 'sec-loans-income-info-net-salary-form-label',
      })
    })

    omitWhen(data.occupation !== IfpOccupation.Furlough, () => {
      validateFutureMonthAndYear({
        fieldName: 'estimatedFurloughEndDate',
        dateToValidate: data.estimatedFurloughEndDate,
        formatMessage,
        minimumYear: MINIMUM_REASONABLE_YEAR,
        isRequired: true,
      })

      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
        incomeAfterTaxesField: 'currentIncomeAfterTaxes',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      })

      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'normalGrossSalary',
        incomeAfterTaxesField: 'normalNetSalary',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
      })
    })

    omitWhen(data.occupation !== IfpOccupation.Entrepreneur, () => {
      validateShortTextInput('companyName', formatMessage, data.companyName, SHORT_TEXT_INPUT_MAX_LENGTH, true)
      validateBusinessId({ fieldName: 'businessId', formatMessage, idToValidate: data.businessId, isRequired: true })

      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'incomeBeforeTaxes',
        incomeAfterTaxesField: 'incomeAfterTaxes',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-income-before-taxes-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-income-after-taxes-label',
      })

      test('legalFormOfTheCompany', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.legalFormOfTheCompany).inside(Object.values(IfpCompanyForm))
      })

      validatePastMonthAndYear({
        fieldName: 'companyFoundingDate',
        dateToValidate: data.companyFoundingDate,
        formatMessage,
        minimumYear: MINIMUM_REASONABLE_YEAR,
        isRequired: true,
      })
    })

    omitWhen(data.occupation !== IfpOccupation.LongtermSickLeave, () => {
      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
        incomeAfterTaxesField: 'currentIncomeAfterTaxes',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      })

      test('hasJobToReturnTo', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.hasJobToReturnTo).inside(Object.values(YesNo))
      })

      omitWhen(data.hasJobToReturnTo !== YesNo.Yes, () => {
        validateFutureMonthAndYear({
          fieldName: 'estimatedReturnToWorkDate',
          dateToValidate: data.estimatedReturnToWorkDate,
          formatMessage,
          minimumYear: MINIMUM_REASONABLE_YEAR,
          isRequired: true,
        })

        validateIncomeFields({
          formatMessage,
          formData: data,
          incomeBeforeTaxesField: 'normalGrossSalary',
          incomeAfterTaxesField: 'normalNetSalary',
          beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
          afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
        })
      })

      omitWhen(data.hasJobToReturnTo !== YesNo.No, () => {
        test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
        })

        omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
          test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
            enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
          })
        })
      })
    })

    omitWhen(data.occupation !== IfpOccupation.Unemployed, () => {
      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
        incomeAfterTaxesField: 'currentIncomeAfterTaxes',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      })

      test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
      })

      omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
        test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
        })
      })
    })

    omitWhen(data.occupation !== IfpOccupation.OnFamilyLeave, () => {
      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
        incomeAfterTaxesField: 'currentIncomeAfterTaxes',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      })

      test('hasJobToReturnTo', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.hasJobToReturnTo).inside(Object.values(YesNo))
      })

      omitWhen(data.hasJobToReturnTo !== YesNo.Yes, () => {
        validateFutureMonthAndYear({
          fieldName: 'estimatedReturnToWorkDate',
          dateToValidate: data.estimatedReturnToWorkDate,
          formatMessage,
          minimumYear: MINIMUM_REASONABLE_YEAR,
          isRequired: true,
        })

        validateIncomeFields({
          formatMessage,
          formData: data,
          incomeBeforeTaxesField: 'normalGrossSalary',
          incomeAfterTaxesField: 'normalNetSalary',
          beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
          afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
        })
      })

      omitWhen(data.hasJobToReturnTo !== YesNo.No, () => {
        test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
        })

        omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
          test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
            enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
          })
        })
      })
    })

    omitWhen(data.occupation !== IfpOccupation.StudyLeave, () => {
      validateIncomeFields({
        formatMessage,
        formData: data,
        incomeBeforeTaxesField: 'currentIncomeBeforeTaxes',
        incomeAfterTaxesField: 'currentIncomeAfterTaxes',
        beforeTaxesLabelKey: 'ifp-loans-applicant-info-current-income-before-taxes-label',
        afterTaxesLabelKey: 'ifp-loans-applicant-info-current-income-after-taxes-label',
      })

      test('hasJobToReturnTo', formatMessage({ id: 'validate-required-field-missing' }), () => {
        enforce(data.hasJobToReturnTo).inside(Object.values(YesNo))
      })

      omitWhen(data.hasJobToReturnTo !== YesNo.Yes, () => {
        validateFutureMonthAndYear({
          fieldName: 'estimatedReturnToWorkDate',
          dateToValidate: data.estimatedReturnToWorkDate,
          formatMessage,
          minimumYear: MINIMUM_REASONABLE_YEAR,
          isRequired: true,
        })

        validateIncomeFields({
          formatMessage,
          formData: data,
          incomeBeforeTaxesField: 'normalGrossSalary',
          incomeAfterTaxesField: 'normalNetSalary',
          beforeTaxesLabelKey: 'ifp-loans-applicant-info-normal-gross-salary-label',
          afterTaxesLabelKey: 'ifp-loans-applicant-info-normal-net-salary-label',
        })
      })

      omitWhen(data.hasJobToReturnTo !== YesNo.No, () => {
        test('expectsToBeEmployedAgain', formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(data.expectsToBeEmployedAgain).inside(Object.values(YesNo))
        })

        omitWhen(data.expectsToBeEmployedAgain !== YesNo.Yes, () => {
          test('timeUntilEmployedAgainEstimation', formatMessage({ id: 'validate-required-field-missing' }), () => {
            enforce(data.timeUntilEmployedAgainEstimation).inside(Object.values(TimeUntilEmployedAgainEstimation))
          })
        })
      })
    })

    test('hasOtherIncome', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.hasOtherIncome).inside(Object.values(YesNo))
    })

    omitWhen(data.hasOtherIncome !== YesNo.Yes, () => {
      data.otherIncomes?.forEach((otherIncome, index) => {
        test(`otherIncomes.${index}.incomeType`, formatMessage({ id: 'validate-required-field-missing' }), () => {
          enforce(otherIncome.incomeType).inside(Object.values(SecLoanOtherIncomeType))
        })

        test(`otherIncomes.${index}.amountAfterTaxes`, formatMessage({ id: 'validate-required-field-missing' }), () => {
          testIsValidMonetaryValue(
            `otherIncomes.${index}.amountAfterTaxes`,
            data.otherIncomes?.[index]?.amountAfterTaxes,
            formatMessage,
          )
        })
      })
    })

    testIsValidMonetaryValue('totalSavings', data.totalSavings, formatMessage)

    testIsValidMonetaryValue('ownShareOfHousingExpenses', data.ownShareOfHousingExpenses, formatMessage)

    test('hasCarOwnership', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.hasCarOwnership).inside(Object.values(YesNo))
    })

    test('carOwnershipType', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.carOwnershipType).inside(Object.values(SecLoanCarOwnershipType))
    })

    testIsValidMonetaryValue('totalLoansAmount', data.totalLoansAmount, formatMessage)

    testIsValidMonetaryValue(
      'totalAmountOfLoanRepaymentsPerMonth',
      data.totalAmountOfLoanRepaymentsPerMonth,
      formatMessage,
    )

    test('payingCompanyLoan', formatMessage({ id: 'validate-required-field-missing' }), () => {
      enforce(data.payingCompanyLoan).inside(Object.values(YesNo))
    })

    testIsValidMonetaryValue('totalHousingCompanyLoan', data.totalHousingCompanyLoan, formatMessage)
  })
}

const validateIncomeFields = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends Record<string, any>,
  K1 extends Extract<keyof T, string>,
  K2 extends Extract<keyof T, string>,
>({
  formData,
  incomeBeforeTaxesField,
  incomeAfterTaxesField,
  beforeTaxesLabelKey,
  afterTaxesLabelKey,
  formatMessage,
}: {
  formData: T
  incomeBeforeTaxesField: K1
  incomeAfterTaxesField: K2
  beforeTaxesLabelKey: string
  afterTaxesLabelKey: string
  formatMessage: IntlShape['formatMessage']
}) => {
  const incomeBeforeTaxesValue = formData[incomeBeforeTaxesField]
  const incomeAfterTaxesValue = formData[incomeAfterTaxesField]
  testIsValidMonetaryValue(incomeBeforeTaxesField, incomeBeforeTaxesValue, formatMessage)
  testIsValidMonetaryValue(incomeAfterTaxesField, incomeAfterTaxesValue, formatMessage)

  omitWhen(!incomeBeforeTaxesValue || !incomeAfterTaxesValue, () => {
    test(
      incomeAfterTaxesField,
      formatMessage(
        { id: 'sec-loans-validate-value-is-not-more-than-other-value' },
        {
          firstValue: formatMessage({ id: beforeTaxesLabelKey }),
          secondValue: formatMessage({ id: afterTaxesLabelKey }),
        },
      ),
      () => {
        const incomeAfterTaxes = assertValue(toNumber(incomeAfterTaxesValue), 'incomeAfterTaxesValue')
        const incomeBeforeTaxes = assertValue(toNumber(incomeBeforeTaxesValue), 'incomeBeforeTaxesValue')
        enforce(incomeBeforeTaxes >= incomeAfterTaxes).isTruthy()
      },
    )
  })
}
